<template>
    <form class="mt-4" :aria-busy="saving ? 'true' : 'false'" @submit.prevent="submit">
        <div
            v-if="summaryMessages.length"
            :id="summaryId"
            ref="errorSummary"
            tabindex="-1"
            class="mb-4 rounded-xl border border-foxy-error/30 p-3 text-sm text-foxy-error outline-none"
            role="alert"
        >
            <p v-for="message in summaryMessages" :key="message">{{ message }}</p>
        </div>

        <section :aria-labelledby="ratingSectionTitleId" class="mt-5 border-t border-foxy-border pt-4">
            <h6 :id="ratingSectionTitleId" class="text-sm font-extrabold text-foxy-text">Оцінка</h6>
            <p :id="ratingHelpId" class="mt-2 text-xs text-foxy-text-secondary">Оцінка цього прочитання.</p>

            <rating-input
                ref="ratingInput"
                class="mt-3"
                :value="draft.rating"
                label="Оцінка цього прочитання"
                :clearable="false"
                :disabled="saving"
                :invalid="Boolean(validationErrors.rating)"
                :error-id="ratingErrorId"
                :described-by="ratingDescriptionIds"
                @input="updateRating"
            />

            <button
                v-if="canClearRating"
                type="button"
                class="mt-2 min-h-11 rounded-lg px-3 text-sm font-semibold text-foxy-error underline decoration-transparent underline-offset-4 transition hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foxy-action disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Прибрати оцінку цього прочитання"
                :disabled="saving"
                @click="clearRating"
            >
                Прибрати оцінку
            </button>

            <p v-if="linkedPublicationDisclosure" :id="ratingDisclosureId" class="mt-2 text-xs text-foxy-text-secondary">
                {{ linkedPublicationDisclosure }}
            </p>
            <p v-if="ratingClearBlockedMessage" :id="ratingClearBlockedId" class="mt-2 text-xs text-foxy-text-secondary">
                {{ ratingClearBlockedMessage }}
            </p>
            <p v-if="validationErrors.rating" :id="ratingErrorId" class="mt-2 text-sm text-foxy-error" role="alert">
                {{ validationErrors.rating }}
            </p>
        </section>

        <library-reading-date-editor
            ref="dateSection"
            :reading-id="snapshot.readingId"
            :state="reading.state"
            :started="draft.started"
            :finished="draft.finished"
            :saving="saving"
            :errors="dateErrors"
            :date-range-error-id="validationErrors.dateRange ? summaryId : ''"
            @update="updateDate"
            @clear-errors="clearErrors"
        />

        <library-reading-language-editor
            ref="languageSection"
            :reading-id="snapshot.readingId"
            :languages="draft.languages"
            :primary-language-code="draft.primary_language_code"
            :canonical-languages="snapshot.languages"
            :preferences-state="preferencesState"
            :saving="saving"
            :errors="validationErrors.language"
            @update="updateLanguages"
            @clear-errors="clearErrors"
            @retry-preferences="$emit('retry-preferences')"
        />

        <library-reading-format-editor
            ref="formatSection"
            :reading-id="snapshot.readingId"
            :rows="draft.formatRows"
            :saving="saving"
            :errors="validationErrors.format"
            @update="updateFormats"
            @clear-errors="clearErrors"
        />

        <div class="mt-5 flex flex-wrap justify-end gap-2 border-t border-foxy-border pt-4">
            <button type="button" class="acc-btn min-h-11" :disabled="saving" @click="cancel">Скасувати</button>
            <button
                ref="saveButton"
                type="submit"
                :class="[saveButtonClass, 'min-h-11 disabled:cursor-not-allowed disabled:opacity-60']"
                :disabled="saveDisabled"
            >
                <span aria-live="polite">{{ saving ? 'Збереження…' : 'Зберегти' }}</span>
            </button>
        </div>
    </form>
</template>

<script lang="ts">
import LibraryReadingDateEditor from './LibraryReadingDateEditor.vue';
import LibraryReadingFormatEditor from './LibraryReadingFormatEditor.vue';
import LibraryReadingLanguageEditor from './LibraryReadingLanguageEditor.vue';
import RatingInput from '../../components/RatingInput.vue';
import { normalizeRating } from '../../utils/rating';

const emptyPartial = () => ({ precision: 'unknown', year: null, month: null, day: null });
const emptyErrors = () => ({
    form: '',
    language: { membership: '', primary: '' },
    started: { group: '', precision: '', year: '', month: '', day: '' },
    finished: { group: '', precision: '', year: '', month: '', day: '' },
    dateRange: '',
    format: { group: '', byCode: {} },
    rating: '',
});

function cloneLanguages(languages) {
    return (Array.isArray(languages) ? languages : []).map((language) => ({ ...language }));
}

function cloneFormatRows(rows) {
    return (Array.isArray(rows) ? rows : []).map((row) => ({ ...row }));
}

export default {
    name: 'LibraryReadingInstanceEditor',
    components: { LibraryReadingDateEditor, LibraryReadingFormatEditor, LibraryReadingLanguageEditor, RatingInput },
    props: {
        reading: { type: Object, required: true },
        readingStatusLockVersion: { type: [Number, String], required: true },
        preferencesState: { type: Object, default: () => ({ status: 'idle', data: null, error: '' }) },
        formatOptions: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        errors: { type: Object, default: emptyErrors },
    },
    data() {
        return {
            snapshot: {
                readingId: null,
                readingStatusLockVersion: null,
                readingInstanceLockVersion: null,
                languages: [],
                primary_language_code: null,
                started: emptyPartial(),
                finished: emptyPartial(),
                formats: [],
                rating: null,
                backsCanonicalRating: false,
            },
            draft: {
                languages: [],
                primary_language_code: null,
                started: emptyPartial(),
                finished: emptyPartial(),
                formatRows: [],
                rating: null,
            },
            clientErrors: emptyErrors(),
        };
    },
    computed: {
        summaryId() {
            return `reading-${this.snapshot.readingId}-editor-error-summary`;
        },
        languageDirty() {
            const snapshotCodes = this.snapshot.languages.map((language) => this.identity(language.code)).sort();
            const draftCodes = this.draft.languages.map((language) => this.identity(language.code)).sort();
            return (
                snapshotCodes.join('|') !== draftCodes.join('|') ||
                this.identity(this.snapshot.primary_language_code) !== this.identity(this.draft.primary_language_code)
            );
        },
        dateDirty() {
            return !this.partialsEqual(this.snapshot.started, this.draft.started) || !this.partialsEqual(this.snapshot.finished, this.draft.finished);
        },
        formatDirty() {
            return this.formatSignature(this.snapshot.formats) !== this.formatSignature(this.draft.formatRows);
        },
        ratingDirty() {
            return normalizeRating(this.draft.rating) !== normalizeRating(this.snapshot.rating);
        },
        dirtyRatingClear() {
            return this.ratingDirty && this.snapshot.rating !== null && this.draft.rating === null;
        },
        requiresRatingClearConfirmation() {
            return this.dirtyRatingClear && this.snapshot.backsCanonicalRating;
        },
        combinedDirty() {
            return this.languageDirty || this.dateDirty || this.formatDirty || this.ratingDirty;
        },
        saveDisabled() {
            return this.saving || !this.combinedDirty;
        },
        saveButtonClass() {
            return this.combinedDirty ? 'acc-btn-primary' : 'acc-btn';
        },
        validationErrors() {
            const server = this.errors || emptyErrors();
            const merged = emptyErrors();
            merged.form = this.clientErrors.form || server.form || '';
            merged.dateRange = this.clientErrors.dateRange || server.dateRange || '';
            merged.rating = this.clientErrors.rating || server.rating || '';
            merged.format.group = this.clientErrors.format.group || server.format?.group || '';
            const formatErrorCodes = new Set([...Object.keys(server.format?.byCode || {}), ...Object.keys(this.clientErrors.format.byCode || {})]);
            for (const code of formatErrorCodes) {
                merged.format.byCode[code] = {
                    code: this.clientErrors.format.byCode?.[code]?.code || server.format?.byCode?.[code]?.code || '',
                    customLabel: this.clientErrors.format.byCode?.[code]?.customLabel || server.format?.byCode?.[code]?.customLabel || '',
                };
            }
            for (const key of ['membership', 'primary']) {
                merged.language[key] = this.clientErrors.language[key] || server.language?.[key] || '';
            }
            for (const dateKey of ['started', 'finished']) {
                for (const component of ['group', 'precision', 'year', 'month', 'day']) {
                    merged[dateKey][component] = this.clientErrors[dateKey][component] || server[dateKey]?.[component] || '';
                }
            }
            return merged;
        },
        summaryMessages() {
            return [...new Set([this.validationErrors.form, this.validationErrors.dateRange].filter(Boolean))];
        },
        dateErrors() {
            return {
                started: this.validationErrors.started,
                finished: this.validationErrors.finished,
                dateRange: this.validationErrors.dateRange,
            };
        },
        finishReadOnly() {
            return ['active', 'paused'].includes(this.reading.state);
        },
        ratingSectionTitleId() {
            return `reading-${this.snapshot.readingId}-rating-title`;
        },
        ratingHelpId() {
            return `reading-${this.snapshot.readingId}-rating-help`;
        },
        ratingDisclosureId() {
            return `reading-${this.snapshot.readingId}-rating-disclosure`;
        },
        ratingClearBlockedId() {
            return `reading-${this.snapshot.readingId}-rating-clear-blocked`;
        },
        ratingErrorId() {
            return `reading-${this.snapshot.readingId}-rating-error`;
        },
        ratingDescriptionIds() {
            return [
                this.ratingHelpId,
                this.linkedPublicationDisclosure ? this.ratingDisclosureId : '',
                this.ratingClearBlockedMessage ? this.ratingClearBlockedId : '',
            ]
                .filter(Boolean)
                .join(' ');
        },
        ratingClearCapability() {
            const capability = this.reading?.rating_clear;
            if (capability && typeof capability === 'object' && !Array.isArray(capability)) {
                if (capability.allowed === true) return { allowed: true, blockedReason: null };
                if (
                    capability.allowed === false &&
                    ['published_publication_requires_rating', 'publication_integrity'].includes(capability.blocked_reason)
                ) {
                    return { allowed: false, blockedReason: capability.blocked_reason };
                }
            }

            return { allowed: false, blockedReason: 'publication_integrity' };
        },
        canClearRating() {
            return this.draft.rating !== null && this.ratingClearCapability.allowed;
        },
        ratingClearBlockedMessage() {
            if (this.draft.rating === null || this.ratingClearCapability.allowed) return '';
            if (this.ratingClearCapability.blockedReason === 'published_publication_requires_rating') {
                if (this.reading.publication?.type === 'review') {
                    return 'Цю оцінку не можна прибрати, доки вона використовується в опублікованій рецензії.';
                }
                if (this.reading.publication?.type === 'opinion') {
                    return 'Цю оцінку не можна прибрати, доки вона використовується в опублікованому відгуку.';
                }
            }

            return 'Не вдалося перевірити пов’язану публікацію. Оновіть дані та повторіть дію.';
        },
        linkedPublicationDisclosure() {
            const publication = this.reading.publication;
            if (publication?.rating_syncs_with_reading !== true) return '';

            return publication.type === 'opinion'
                ? 'Після збереження ця оцінка також оновиться у вашому відгуку.'
                : 'Після збереження ця оцінка також оновиться у вашій рецензії.';
        },
    },
    watch: {
        'reading.id'(next, previous) {
            if (Number(next) !== Number(previous)) this.resetToCanonical();
        },
        combinedDirty(value) {
            this.$emit('dirty-change', value);
        },
    },
    created() {
        this.resetToCanonical();
    },
    mounted() {
        this.focusInitialControl();
    },
    methods: {
        identity(code) {
            return String(code || '')
                .trim()
                .toLowerCase();
        },
        normalizePartial(partial) {
            const source = partial || {};
            const precision = ['unknown', 'year', 'month', 'day'].includes(source.precision) ? source.precision : 'unknown';
            const value = {
                precision,
                year: source.year === null || source.year === undefined || source.year === '' ? null : String(source.year),
                month: source.month === null || source.month === undefined || source.month === '' ? null : Number(source.month),
                day: source.day === null || source.day === undefined || source.day === '' ? null : Number(source.day),
            };
            if (precision === 'unknown') return emptyPartial();
            if (precision === 'year') return { ...value, month: null, day: null };
            if (precision === 'month') return { ...value, day: null };
            return value;
        },
        partialsEqual(left, right) {
            return JSON.stringify(this.normalizePartial(left)) === JSON.stringify(this.normalizePartial(right));
        },
        normalizeCustomLabel(label) {
            const normalized = String(label ?? '').trim();
            return normalized === '' ? null : normalized;
        },
        normalizeCanonicalFormats(formats) {
            const seen = new Set();
            return (Array.isArray(formats) ? formats : []).flatMap((format) => {
                const code = this.identity(format?.code);
                if (!code || seen.has(code)) return [];
                seen.add(code);
                return [
                    {
                        code,
                        customLabel: code === 'other' ? this.normalizeCustomLabel(format?.custom_label) : null,
                    },
                ];
            });
        },
        buildFormatRows(options, canonicalFormats) {
            const selectedByCode = new Map();
            for (const format of Array.isArray(canonicalFormats) ? canonicalFormats : []) {
                const code = this.identity(format?.code);
                if (!code || selectedByCode.has(code)) continue;
                selectedByCode.set(code, format);
            }

            const rows = [];
            const seen = new Set();
            for (const option of Array.isArray(options) ? options : []) {
                const code = this.identity(option?.code);
                if (!code || seen.has(code)) continue;
                seen.add(code);
                const canonical = selectedByCode.get(code);
                rows.push({
                    code,
                    label: option.label || option.code || code,
                    isActiveOption: true,
                    wasCanonicalSelection: Boolean(canonical),
                    selected: Boolean(canonical),
                    customLabel: code === 'other' ? String(canonical?.custom_label ?? '') : '',
                });
            }
            for (const format of Array.isArray(canonicalFormats) ? canonicalFormats : []) {
                const code = this.identity(format?.code);
                if (!code || seen.has(code)) continue;
                seen.add(code);
                rows.push({
                    code,
                    label: format.label || format.code || code,
                    isActiveOption: false,
                    wasCanonicalSelection: true,
                    selected: true,
                    customLabel: code === 'other' ? String(format.custom_label ?? '') : '',
                });
            }
            return rows;
        },
        formatSignature(formats) {
            const signature = (Array.isArray(formats) ? formats : [])
                .filter((format) => !Object.prototype.hasOwnProperty.call(format, 'selected') || format.selected)
                .map((format) => {
                    const code = this.identity(format.code);
                    const label = code === 'other' ? this.normalizeCustomLabel(format.customLabel ?? format.custom_label) : null;
                    return [code, label];
                })
                .filter(([code]) => Boolean(code))
                .sort(([left], [right]) => left.localeCompare(right));
            return JSON.stringify(signature);
        },
        resetToCanonical() {
            const started = this.normalizePartial(this.reading.start);
            const finished = this.finishReadOnly ? emptyPartial() : this.normalizePartial(this.reading.finish);
            const formats = this.normalizeCanonicalFormats(this.reading.formats);
            const rating = normalizeRating(this.reading.rating);
            this.snapshot = {
                readingId: this.reading.id,
                readingStatusLockVersion: Number(this.readingStatusLockVersion),
                readingInstanceLockVersion: Number(this.reading.lock_version),
                languages: cloneLanguages(this.reading.languages),
                primary_language_code: this.reading.primary_language_code ?? null,
                started,
                finished,
                formats,
                rating,
                backsCanonicalRating: this.reading.backs_canonical_rating === true,
            };
            this.draft = {
                languages: cloneLanguages(this.snapshot.languages),
                primary_language_code: this.snapshot.primary_language_code,
                started: { ...started },
                finished: { ...finished },
                formatRows: this.buildFormatRows(this.formatOptions, this.reading.formats),
                rating,
            };
            this.clientErrors = emptyErrors();
            this.$refs.languageSection?.resetInteraction();
            this.$emit('dirty-change', false);
        },
        updateLanguages(update) {
            this.draft = {
                ...this.draft,
                languages: cloneLanguages(update.languages),
                primary_language_code: update.primaryLanguageCode ?? null,
            };
            this.clientErrors.language = { membership: '', primary: '' };
            this.clearErrors();
        },
        updateDate(update) {
            if (!['started', 'finished'].includes(update?.key)) return;
            if (update.key === 'finished' && this.finishReadOnly) return;
            this.draft = { ...this.draft, [update.key]: this.normalizePartial(update.value) };
            this.clientErrors[update.key] = { group: '', precision: '', year: '', month: '', day: '' };
            this.clientErrors.dateRange = '';
            this.clearErrors();
        },
        updateFormats(rows) {
            this.draft = { ...this.draft, formatRows: cloneFormatRows(rows) };
            this.clientErrors.format = { group: '', byCode: {} };
            this.clearErrors();
        },
        updateRating(value) {
            const rating = normalizeRating(value);
            if (rating === null) return;
            this.draft = { ...this.draft, rating };
            this.clientErrors.rating = '';
            this.clearErrors();
        },
        clearRating() {
            if (!this.canClearRating || this.saving) return;
            this.draft = { ...this.draft, rating: null };
            this.clientErrors.rating = '';
            this.clearErrors();
            this.$nextTick(() => this.$refs.ratingInput?.focus());
        },
        clearErrors() {
            this.$emit('clear-errors');
        },
        validYear(year) {
            return /^\d{4}$/.test(String(year || '')) && Number(year) >= 1000 && Number(year) <= 9999;
        },
        daysInMonth(year, month) {
            if (!this.validYear(year) || !Number.isInteger(Number(month)) || Number(month) < 1 || Number(month) > 12) return 0;
            return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
        },
        validatePartial(key) {
            const value = this.draft[key];
            if (value.precision === 'unknown') return true;
            let valid = true;
            if (!this.validYear(value.year)) {
                this.clientErrors[key].year = 'Вкажіть рік від 1000 до 9999.';
                valid = false;
            }
            if (['month', 'day'].includes(value.precision) && (!Number.isInteger(value.month) || value.month < 1 || value.month > 12)) {
                this.clientErrors[key].month = 'Оберіть місяць.';
                valid = false;
            }
            if (value.precision === 'day') {
                const maximum = this.daysInMonth(value.year, value.month);
                if (!Number.isInteger(value.day) || value.day < 1 || value.day > maximum) {
                    this.clientErrors[key].day = 'Оберіть дійсний день.';
                    valid = false;
                }
            }
            return valid;
        },
        firstValidationTarget() {
            const errors = this.validationErrors;
            if (errors.language.primary) return 'language.primary';
            if (errors.language.membership) return 'language.membership';
            for (const key of ['started', 'finished']) {
                for (const component of ['precision', 'year', 'month', 'day', 'group']) {
                    if (errors[key][component]) return `${key}.${component}`;
                }
            }
            if (errors.format.group) return 'format.group';
            for (const row of this.draft.formatRows) {
                const code = this.identity(row.code);
                const itemErrors = errors.format.byCode?.[code];
                if (itemErrors?.code) return `format.${encodeURIComponent(code)}.code`;
                if (itemErrors?.customLabel) return `format.${encodeURIComponent(code)}.customLabel`;
            }
            if (errors.rating) return 'rating';
            if (errors.dateRange) return 'dateRange';
            if (errors.form) return 'form';
            return '';
        },
        validate() {
            this.clientErrors = emptyErrors();
            let valid = true;
            if (!this.$refs.languageSection?.validate()) {
                this.clientErrors.language.primary = 'Оберіть основну мову читання.';
                valid = false;
            }
            if (!this.validatePartial('started')) valid = false;
            if (!this.finishReadOnly && !this.validatePartial('finished')) valid = false;
            if (this.ratingDirty && this.draft.rating !== null && normalizeRating(this.draft.rating) === null) {
                this.clientErrors.rating = 'Оберіть оцінку від 1 до 10.';
                valid = false;
            }
            if (!valid) this.focusValidation(this.firstValidationTarget());
            return valid;
        },
        serializePartial(value) {
            const normalized = this.normalizePartial(value);
            return {
                precision: normalized.precision,
                year: normalized.year === null ? null : Number(normalized.year),
                month: normalized.month,
                day: normalized.day,
            };
        },
        serializeFormats() {
            return this.draft.formatRows
                .filter((row) => row.selected)
                .map((row) => {
                    const code = this.identity(row.code);
                    return {
                        code,
                        custom_label: code === 'other' ? this.normalizeCustomLabel(row.customLabel) : null,
                    };
                });
        },
        submit() {
            if (this.saving || !this.combinedDirty || !this.validate()) return;
            const intent = {
                readingId: this.snapshot.readingId,
                expectedReadingStatusLockVersion: this.snapshot.readingStatusLockVersion,
                expectedReadingInstanceLockVersion: this.snapshot.readingInstanceLockVersion,
            };
            if (this.languageDirty) {
                intent.languageCodes = this.draft.languages.map((language) => language.code);
                intent.primaryLanguageCode = this.draft.languages.length ? this.draft.primary_language_code : null;
            }
            if (this.dateDirty) {
                intent.started = this.serializePartial(this.draft.started);
                intent.finished = this.finishReadOnly ? emptyPartial() : this.serializePartial(this.draft.finished);
            }
            if (this.formatDirty) intent.formats = this.serializeFormats();
            if (this.ratingDirty) {
                intent.rating = this.draft.rating === null ? null : normalizeRating(this.draft.rating);
                if (this.requiresRatingClearConfirmation) intent.requiresRatingClearConfirmation = true;
            }
            this.$emit('save', intent);
        },
        cancel() {
            this.$emit('cancel', this.snapshot.readingId);
        },
        focusInitialControl() {
            this.$nextTick(() => this.$refs.languageSection?.focusInitialControl());
        },
        focusValidation(target) {
            this.$nextTick(() => {
                if (target === 'form' || target === 'dateRange') {
                    this.$refs.errorSummary?.focus();
                    return;
                }
                if (String(target).startsWith('language.')) {
                    this.$refs.languageSection?.focusValidation(String(target).split('.')[1]);
                    return;
                }
                if (String(target).startsWith('format.')) {
                    const [, encodedCode, field] = String(target).split('.');
                    this.$refs.formatSection?.focusValidation(encodedCode === 'group' ? '' : decodeURIComponent(encodedCode), field || 'group');
                    return;
                }
                if (target === 'rating') {
                    this.$refs.ratingInput?.focus();
                    return;
                }
                this.$refs.dateSection?.focusValidation(target);
            });
        },
    },
};
</script>
